import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import {
  buildScoringInputFromObject,
  calculateScore,
  type ScoringInput,
  type ScoringResult,
} from "@/lib/scoring-engine";
import { useMarketCalibration, metroFromContext } from "@/lib/market-calibration";
import { buildDocumentIntelligenceContext } from "@/lib/document-intelligence";
import { buildDealExportIntelligence, type DealExportIntelligence } from "@/lib/deal-share-summary";
import {
  normalizeDealIntelligence,
  type CanonicalDealIntelligence,
} from "@/lib/deal-intelligence-normalizer";

interface UseDealContextResult {
  context: Record<string, unknown>;
  documents: ReturnType<typeof buildDocumentIntelligenceContext>;
  exportIntelligence: DealExportIntelligence;
  scoringInput: ScoringInput;
  scoringResult: ScoringResult;
  /** Canonical, normalized deal intelligence — single source of truth. */
  intelligence: CanonicalDealIntelligence | null;
  dealId: string;
  dealName: string;
  loading: boolean;
  error: string | null;
}

export function useDealContext(dealId: string): UseDealContextResult {
  const [context, setContext] = useState<Record<string, unknown>>({});
  const [documents, setDocuments] = useState<ReturnType<typeof buildDocumentIntelligenceContext>>({ documents: [], by_lane: {} });
  const [exportIntelligence, setExportIntelligence] = useState<DealExportIntelligence>(buildDealExportIntelligence([]));
  const [dealName, setDealName] = useState<string>("");
  const [intelligence, setIntelligence] = useState<CanonicalDealIntelligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) {
      setLoading(false);
      setError("No deal ID provided");
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);

      const { data: deal, error: fetchError } = await supabase
        .from("deals")
        .select("name, context, tier1_inputs")
        .eq("id", dealId)
        .single();

      const [{ data: dealDocuments }, { data: intelRow }] = await Promise.all([
        supabase
          .from("deal_documents")
          .select("id, doc_type, document_lane, document_subtype, risk_flags, recommended_underwriting_adjustments, confidence, executive_interpretation, brickshare_field_mapping, evidence_log, extracted_data")
          .eq("deal_id", dealId)
          .order("created_at", { ascending: false }),
        supabase
          .from("deal_intelligence")
          .select("*")
          .eq("deal_id", dealId)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (fetchError || !deal) {
        setError(fetchError?.message || "Deal not found");
        setLoading(false);
        return;
      }

      // Merge context with tier1_inputs (tier1 has quick screen financials)
      const ctx = { ...(((deal.context as Record<string, unknown>) || {})) };
      if (deal.tier1_inputs && typeof deal.tier1_inputs === "object") {
        Object.assign(ctx, deal.tier1_inputs as Record<string, unknown>);
      }

      const docRows = (dealDocuments || []) as Tables<"deal_documents">[];
      const t12Docs = docRows.filter((doc) => doc.document_lane === "t12");
      const rentRollDocs = docRows.filter((doc) => doc.document_lane === "rent_roll");
      if (t12Docs.length > 0) ctx.t12_file = t12Docs;
      if (rentRollDocs.length > 0) ctx.rent_roll_file = rentRollDocs;

      const documentContext = buildDocumentIntelligenceContext(docRows);

      setContext(ctx);
      setDocuments(documentContext);
      setExportIntelligence(buildDealExportIntelligence(documentContext.documents));
      setDealName((deal.name as string) ?? "");
      setIntelligence(
        intelRow ? normalizeDealIntelligence(intelRow as unknown as Record<string, unknown>) : null,
      );
      setLoading(false);
    };

    load();
  }, [dealId]);

  // Attached here, at the single producer, so every V2 surface stresses the
  // deal against the same metro-calibrated grid the orchestrator scored it
  // with. First render uses the standard grid until the row arrives — the
  // same value a deal with no benchmark keeps permanently.
  const calibration = useMarketCalibration(metroFromContext(context));
  const scoringInput = useMemo(
    () => ({ ...buildScoringInputFromObject(context), market_calibration: calibration }),
    [context, calibration],
  );
  const scoringResult = useMemo(() => calculateScore(scoringInput), [scoringInput]);

  return { context, documents, exportIntelligence, scoringInput, scoringResult, intelligence, dealId, dealName, loading, error };
}
